import { sortByProperty, tagCleanser } from '@ups/xplat/utils';
import {
  dynamicModelFormArrayTypes,
  IDynamicContainerMetadata,
  IDynamicControl,
  IDynamicControlResponse,
  IDynamicModel,
  IDynamicPageOptions,
  IDynamicResponseBody,
} from '../models';
import {
  dynamicFindControlResponseByCondition,
  dynamicGetCategoryTagName,
  dynamicGetCategoryTags,
  prepareFormContainerControl,
} from '../utils';

/** A saved response's value as the control shows it (WI 31925: split out of DynamicRenderService, unchanged). */
export function prepareDynamicResponseValue(
  response?: IDynamicControlResponse | null
) {
  switch (response?.dynamicControlType) {
    case 'checkbox':
      // database saves as strings, convert to boolean values
      return response?.responseContent?.toLowerCase() === 'true';
  }
  return response?.responseContent;
}

/** The two DynamicRenderService members a saved response is applied through; the service passes itself. */
export interface IDynamicResponseContentHost {
  applyResponseContent(
    control: IDynamicControl,
    dynamicResponse: IDynamicResponseBody,
    options?: IDynamicPageOptions
  ): void;
  prepareResponseValue(
    response?: IDynamicControlResponse | null
  ): string | boolean | undefined;
}

/** Shows a saved response's value on `control` and its children (through `host`, as before), read-only. */
export function applyDynamicResponseContent(
  host: IDynamicResponseContentHost,
  control: IDynamicControl,
  dynamicResponse: IDynamicResponseBody,
  options?: IDynamicPageOptions
) {
  const response = dynamicFindControlResponseByCondition(
    dynamicResponse.dynamicControlResponses || [],
    options?.isReclassification
      ? (cr) => cr.dynamicControlName === control.dynamicControlName
      : (cr) => cr.dynamicControlId === control.dynamicControlId
  );
  control.value = host.prepareResponseValue(response);
  control.options.initWithQuery = response?.responseContent;
  control.disabled = !options?.isEditReview && !options?.isReclassification;
  control.readOnly = true;
  for (const childControl of control.dynamicControls || []) {
    host.applyResponseContent(childControl, dynamicResponse, options);
  }
}

/** The route of the form `options.name` names, from the published list or else the full list; null when neither has it. */
export function dynamicFormPath(
  published: Array<IDynamicContainerMetadata>,
  all: Array<IDynamicContainerMetadata>,
  options: IDynamicPageOptions
): string | null {
  if (options?.name) {
    let form = published.find(
      (f) => tagCleanser(f.dynamicContainerName || '') === options.name
    );
    if (!form) {
      // unpublished form
      form = all.find(
        (f) => tagCleanser(f.dynamicContainerName || '') === options.name
      );
      if (form && !form.url) {
        const tags = dynamicGetCategoryTags(form.tags || []);
        const categoryName = tags.length
          ? dynamicGetCategoryTagName(tags[0])
          : '';
        const namePath = tagCleanser(form.dynamicContainerName || '');
        form.url = `/forms1/${tagCleanser(categoryName)}/${namePath}`;
      }
    }
    if (form && form.url) {
      let url = form.url;
      if (options.dynamicResponseId) {
        url += `?dynamicResponseId=${options.dynamicResponseId}`;
        if (options.shortName) {
          url += `&shortName=${options.shortName}`;
        }
        if (options.isCoaching) {
          url += '&isCoaching=1';
        }
        if (options.isEditReview) {
          url += '&isEditReview=1';
        } else if (options.isReview) {
          url += '&isReview=1';
        }
      }
      return url;
    }
  }
  return null;
}

/** An accordion's nested button group: shows its controlled controls when its saved value matches, and maps its buttons. */
function prepareAccordionButtonGroup(
  nestedControl: IDynamicControl,
  nestedControls: Array<IDynamicControl>
) {
  if (
    !nestedControl.options ||
    !nestedControl.options.buttongroup ||
    !nestedControl.options.buttongroup.length
  ) {
    return;
  }
  if (nestedControl.options.showOnValues?.dynamicControlName) {
    const showOnValues = nestedControl.options.showOnValues;
    const controllingDynamicControlName = showOnValues.dynamicControlName;

    if (controllingDynamicControlName) {
      showOnValues.formControlName = controllingDynamicControlName;

      if (
        nestedControl.value !== undefined &&
        showOnValues.values.includes(nestedControl.value)
      ) {
        const dynamicControlNames = showOnValues.dynamicControlNames || [];
        const controlsToShow = nestedControls.filter((nc) => {
          const dynamicControlName = nc.dynamicControlName;

          return (
            !!dynamicControlName &&
            (dynamicControlName === controllingDynamicControlName ||
              dynamicControlNames.includes(dynamicControlName))
          );
        });

        (controlsToShow || []).forEach((controlToShow) => {
          controlToShow.options.hidden = false;
        });
      }
    }
  }
  nestedControl.options.buttongroup = nestedControl.options.buttongroup.map(
    (bg: { dynamicControlLabel?: string; value?: string | number }) => {
      return {
        label: bg.dynamicControlLabel,
        value: bg.value,
      };
    }
  );
}

/** Folds an accordion's nested controls into its options.accordion models. */
function prepareAccordion(c: IDynamicControl, isFormBuilder?: boolean) {
  if (!c.dynamicControls) {
    return;
  }
  const nestedControls = c.dynamicControls.sort(sortByProperty('order', true));
  c.options = c.options || {};
  c.options.accordion = [];
  for (const nestedControl of nestedControls) {
    prepareFormContainerControl(nestedControl, isFormBuilder);

    switch (nestedControl.dynamicControlType) {
      case 'buttongroup':
        prepareAccordionButtonGroup(nestedControl, nestedControls);
        break;
    }
    nestedControl.isFormBuilder = isFormBuilder;
    c.options.accordion.push({
      clientId: nestedControl.dynamicControlId,
      formControlName: nestedControl.dynamicControlName,
      type: nestedControl.dynamicControlType || 'input',
      label: nestedControl.dynamicControlLabel,
      isReusable: nestedControl.isReusable,
      ...nestedControl,
    });
  }
  delete c.dynamicControls;
}

/** The trailing controls a page's options ask for: comments, coaching, review, draft submit, last modified. */
function pageOptionControls(
  container: Partial<IDynamicContainerMetadata>,
  options: IDynamicPageOptions | undefined,
  formPath: string | undefined
): IDynamicModel[] {
  const controls: IDynamicModel[] = [];
  if (options?.showComments && !options?.isDraft) {
    controls.push({
      formControlName: 'comment',
      type: 'comment',
      options: {
        dynamicContainerId: container.dynamicContainerId,
        dynamicContainerResponseId: options?.dynamicResponseId,
        formPath,
      },
    });
  }

  if (!options?.isDraft) {
    if (options?.isCoaching) {
      controls.push({
        formControlName: 'coaching',
        type: 'coaching',
        options: {
          dynamicContainerId: container.dynamicContainerId,
          dynamicContainerResponseId: options?.dynamicResponseId,
          deleteComment: options?.deleteComment,
          submittedUserId: options.submittedUserId,
          isReadOnly: options.isReadOnly,
        },
      });
    }
    if (options?.isAICoaching) {
      controls.push({
        formControlName: 'ai-coaching',
        type: 'ai-coaching',
        options: {
          dynamicContainerId: container.dynamicContainerId,
          dynamicContainerResponseId: options?.dynamicResponseId,
          deleteComment: options?.deleteComment,
          submittedUserId: options.submittedUserId,
          isReadOnly: options.isReadOnly,
        },
      });
    }
    if ((options?.isReview || options?.isEditReview) && !options?.isDraft) {
      controls.push({
        formControlName: 'review',
        type: 'review',
        options: {
          dynamicContainerId: container.dynamicContainerId,
          dynamicContainerResponseId: options?.dynamicResponseId,
          isEditReview: options?.isEditReview,
          reviewId: options?.reviewId,
          deleteComment: options?.deleteComment,
          submittedUserId: options.submittedUserId,
          isReadOnly: options.isReadOnly,
        },
      });
    }
  }

  if (options?.isDraft) {
    controls.push({
      formControlName: 'submit',
      type: 'button',
      value: 'Submit',
      // order,
      options: {
        showCancelButton: true,
        showDraftButton: true,
        showDeleteDraftButton: true,
        primary: true,
        submit: true,
      },
    });
  }

  if (options?.showLastModified) {
    // Add last modified data component
    controls.push({
      formControlName: 'last-modified',
      type: 'last-modified',
    });
  }
  return controls;
}

/**
 * The dynamic models a container renders as, with a saved response's values applied when one is given
 * (WI 31925: the body of DynamicRenderService.prepareContainerForDisplay, unchanged).
 */
export function buildDynamicDisplayControls(
  host: IDynamicResponseContentHost,
  container: Partial<IDynamicContainerMetadata>,
  isFormBuilder: boolean | undefined,
  options: IDynamicPageOptions | undefined,
  formPathOf: () => string | undefined,
  dynamicResponse?: IDynamicResponseBody
): IDynamicModel[] {
  const controls: IDynamicModel[] = [];
  const dynamicControls =
    container && container.dynamicControls
      ? container.dynamicControls.sort(sortByProperty('order', true))
      : [];
  const formPath = formPathOf();

  if (options?.dynamicResponseId && !options?.isReclassification) {
    controls.push({
      formControlName: 'copyLinkButton',
      type: 'copyLinkButton',
      options: {
        dynamicContainerId: container.dynamicContainerId,
        dynamicContainerResponseId: options?.dynamicResponseId,
        formPath,
      },
    });
  }

  if (dynamicResponse) {
    if (options) {
      options.submittedUserId = dynamicResponse.submittedBy;
    }
  }

  for (const c of dynamicControls) {
    // legacy option names + WI 34031 form-fill job scope
    prepareFormContainerControl(c, isFormBuilder);

    if (dynamicResponse) {
      host.applyResponseContent(c, dynamicResponse, options);
    }
    // handle certain types based on way backend api stores them
    switch (c.dynamicControlType) {
      case 'accordion':
        prepareAccordion(c, isFormBuilder);
        break;
    }
    const dynamicModel: IDynamicModel = {
      clientId: c.dynamicControlId,
      type: c.dynamicControlType || 'input',
      label: c.dynamicControlLabel,
      instruction: c.dynamicControlInstruction,
      isReusable: c.isReusable,
      ...c,
    };
    if (dynamicModelFormArrayTypes.includes(dynamicModel.type)) {
      dynamicModel.formArrayName = c.dynamicControlName;
    } else {
      dynamicModel.formControlName = c.dynamicControlName;
    }
    if (dynamicModel.type !== 'button') {
      // right now, not supporting custom buttons within containers
      controls.push(dynamicModel);
    }
  }

  return controls.concat(pageOptionControls(container, options, formPath));
}
